// Andrew's Picks - the events he'd name himself.
//
// Everything else in the drawer answers a question about an event that the event
// can answer: what kind is it, what does it cost, where is it, who runs it. A
// pick answers a question only Andrew can, and it's the one a first-time visitor
// most wants answered - "there are 141 things on this week, which of them is
// actually worth my Friday". So the flag lives on the row (see the migration for
// why it isn't a lyh_filter_groups rule) and this file is everything that reads
// or writes it.
//
// The same flag is what the top of a newsletter issue names, which is the reason
// it has to be settable AFTER an event is approved: the issue gets built days
// later than the events get loaded.

import { useCallback, useEffect, useState } from 'react'
import { supabase } from './supabase'
import { saveEventPatch } from './occasions'
import { PUBLIC_EVENT_COLUMNS } from './eventColumns'
import type { EventRow } from '../types'

/**
 * Whether a row is picked, read defensively.
 *
 * `select('*')` returns whatever columns exist, so on a deploy that lands before
 * its migration this is simply absent rather than false. Comparing to true keeps
 * that case at "nothing is picked", which is the state the drawer already knows
 * how to render - it hides the filter - instead of an undefined leaking into a
 * checkbox.
 */
export function isPick(e: Pick<EventRow, 'andrews_pick'>): boolean {
  return e.andrews_pick === true
}

/** Flag or unflag one event. Reads the row back, for the reason saveEventPatch
    documents: an UPDATE that RLS refuses is a silent no-op, not an error. */
export async function savePick(
  id: string,
  pick: boolean,
): Promise<{ error: string | null }> {
  return saveEventPatch(id, { andrews_pick: pick })
}

/**
 * Upcoming approved events, for the picking panel in the approval queue.
 *
 * Upcoming ONLY: picking an event that has already happened cannot help anyone,
 * and including the back catalogue would bury this week's list under three
 * weeks of past ones. Same shape as useTaggableEvents next door, deliberately -
 * they are the same job on a different column.
 */
export function usePickableEvents(enabled: boolean) {
  const [pickable, setPickable] = useState<EventRow[]>([])

  const refetch = useCallback(async () => {
    if (!enabled) {
      setPickable([])
      return
    }
    const { data, error } = await supabase
      .from('lyh_events')
      .select(PUBLIC_EVENT_COLUMNS)
      .eq('status', 'approved')
      .gte('start_time', new Date().toISOString())
      .order('start_time', { ascending: true })
    if (error) {
      console.error('load pickable events', error)
      setPickable([])
      return
    }
    setPickable((data as EventRow[]) ?? [])
  }, [enabled])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { pickable, refetch }
}
